import { TranslatePipe } from "@ngx-translate/core";
import { TranslateService } from "@ngx-translate/core";
import { SvgIconComponent } from "angular-svg-icon";
import { Apollo } from "apollo-angular";

import { ChangeDetectionStrategy, Component, computed, inject, resource, signal } from "@angular/core";

import { TrafficService } from "../../../apis/stats/rest/services";
import { ReadableSizePipe } from "../../../helpers/bitspipe/bitspipe";
import { reportAndReturn } from "../../../helpers/report-to-sentry";
import { AuthService } from "../../../services/authservice";
import {
  COLOR_BLUE,
  COLOR_BROWN,
  COLOR_GREEN,
  COLOR_LIGHT_BLUE,
  COLOR_LIGHT_GREEN,
  COLOR_LIGHT_ORANGE,
  COLOR_LIGHT_PURPLE,
  COLOR_LIGHT_RED,
  COLOR_ORANGE,
  COLOR_PURPLE,
  COLOR_RED,
  COLOR_YELLOW,
} from "../../common/line-chart/colors";
import { ChartCustomization } from "../../common/line-chart/line-chart-configuration";
import { LineChartRadioButtons, RadioOption } from "../../common/line-chart/line-chart-radio-buttons";
import { LineChartSubjectDropdown } from "../../common/line-chart/line-chart-subject-dropdown";
import { ChartMetadata, LineChart, LineChartLines } from "../../common/line-chart/line-chart.component";
import { getDateRange, toLineChartLine } from "../../common/line-chart/line-chart.helpers";
import {
  CertDdosChartSubscription,
  CertDdosChartSubscriptionList,
  SURF_CERT_DDOS_QUERY,
} from "./cert-ddos-line-chart.rgql";

type IpVersion = "4" | "6";
type Unit = "bits" | "packets";
type DdosSubject = { subject: CertDdosChartSubscription; name: string };

@Component({
  selector: "cert-ddos-line-chart",
  templateUrl: "cert-ddos-line-chart.html",
  styleUrl: "cert-ddos-line-chart.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TranslatePipe, LineChartSubjectDropdown, LineChartRadioButtons, LineChart, SvgIconComponent],
})
export class CertDdosLineChart {
  private readonly auth = inject(AuthService);
  private readonly apollo = inject(Apollo);
  private readonly trafficService = inject(TrafficService);
  private readonly translate = inject(TranslateService);

  protected readonly customization = computed<ChartCustomization>(() => ({
    formatPoint: (series: string, y: number): string => {
      const v =
        this.unit() === "bits" ?
          new ReadableSizePipe().transform(y)
        : `${Math.abs(Math.round(y)).toLocaleString()} pps`;
      const meta = this.ddosMetadata()[series];
      return `<span style="color: ${meta?.color ?? "black"}">●</span> ${meta?.label ?? ""}: <b>${v}</b><br/>`;
    },
    noDataText: this.translate.instant("Chart.NoDataFoundReason", {
      reason: this.ddosLoadError() ? "error" : "empty",
    }) as string,
  }));

  protected readonly ipVersion = signal<IpVersion>("4");
  protected readonly ipVersionOptions: RadioOption<IpVersion>[] = [
    { value: "4", label: "IPv4" },
    { value: "6", label: "IPv6" },
  ];

  protected readonly unit = signal<Unit>("bits");
  protected readonly unitOptions: RadioOption<Unit>[] = [
    { value: "bits", label: "Chart.Label.BitsS" },
    { value: "packets", label: "Chart.Label.PacketsS" },
  ];

  protected readonly ddosTraffic = resource({
    params: () => {
      const subject = this.selectedSubject();
      if (!subject) return undefined;
      const dateRange = getDateRange(this.dateRangeOfInterest());
      return {
        subscription_id: subject.subject.subscriptionId,
        ip_version: this.ipVersion(),
        unit: this.unit(),
        ...dateRange,
      };
    },
    loader: async ({ params }): Promise<LineChartLines> => {
      this.ddosLoadError.set(false);
      const data = await this.trafficService.getServiceDdosTraffic(params).catch(reportAndReturn(new Error()));

      if (data instanceof Error) {
        this.ddosLoadError.set(true);
        return {};
      }

      const lines: LineChartLines = {};
      for (const entry of Object.values(data)) {
        if (!entry?.name) continue;
        lines[entry.name] = toLineChartLine(entry.ddos_stat);
      }
      return lines;
    },
    defaultValue: {} as LineChartLines,
  });

  private readonly ddosableServices = resource({
    params: () => ({ customerId: this.auth.viewingCustomer().customerId }),
    loader: ({ params }) =>
      this.apollo
        .query<CertDdosChartSubscriptionList>({
          query: SURF_CERT_DDOS_QUERY,
          variables: { customer: params.customerId },
        })
        .toPromise()
        .then((data) => data?.data?.surfCertList.page.filter(ddosableService) ?? [])
        .catch(reportAndReturn([] as CertDdosChartSubscription[])),
    defaultValue: [] as CertDdosChartSubscription[],
  });

  protected readonly ddosSubjects = computed<DdosSubject[]>(() =>
    this.ddosableServices.value().map((s) => ({
      subject: s,
      name: s.customerDescription ?? s.description,
    })),
  );

  protected readonly selectedSubject = signal<DdosSubject | undefined>(undefined);

  protected readonly ddosLoadError = signal(false);

  protected readonly ddosMetadata = computed<ChartMetadata>(() => {
    const entries = Object.keys(this.ddosTraffic.value()).map((name, index) => [
      name,
      { color: DDOS_COLORS[index % DDOS_COLORS.length], label: formatSerieName(name) },
    ]);
    return Object.fromEntries(entries);
  });

  protected readonly viewMode = computed(() => {
    if (this.ddosTraffic.isLoading()) return "chart" as const;
    if (this.ddosLoadError()) return "custom" as const;
    const lines = this.ddosTraffic.value();
    if (this.ddosTraffic.status() === "resolved" && Object.values(lines).every((v) => v.length === 0)) {
      return "custom" as const;
    }
    return "chart" as const;
  });

  protected readonly dateRangeOfInterest = signal<[Date, Date]>([new Date(Date.now() - 1000 * 3600 * 24), new Date()]);
}

const DDOS_COLORS = [
  COLOR_RED,
  COLOR_BLUE,
  COLOR_GREEN,
  COLOR_PURPLE,
  COLOR_ORANGE,
  COLOR_YELLOW,
  COLOR_BROWN,
  COLOR_LIGHT_RED,
  COLOR_LIGHT_BLUE,
  COLOR_LIGHT_GREEN,
  COLOR_LIGHT_PURPLE,
  COLOR_LIGHT_ORANGE,
];

const formatSerieName = (name: string): string =>
  (name.split("-").pop() ?? name).replace(/_/g, " ").replace("police ", "");

const ddosableService = (s: CertDdosChartSubscription) =>
  (s.productType === "FW" && s.surfcertFilterEnabled) || s.productType === "IP";
